import { useState, useEffect, useCallback } from 'react';
import { Bookmark, Trash2, Loader2, Download } from 'lucide-react';
import {
  listSavedChatResponses,
  deleteSavedChatResponse,
  type SavedChatResponse,
} from '../api/chat';
import { useToastStore } from '../stores/toast';
import { useAuthStore } from '../stores/auth';
import MarkdownRenderer from './MarkdownRenderer';

interface SavedResponsesPanelProps {
  caseId: string;
  /** Bumped by the parent when a new response is saved so this list refreshes. */
  refreshNonce: number;
}

// Show a short preview of the saved content (first ~200 chars / 4 lines).
function preview(content: string): string {
  const lines = content.split('\n').filter((l) => l.trim().length > 0);
  const head = lines.slice(0, 4).join('\n');
  return head.length > 200 ? head.slice(0, 200) + '...' : head;
}

export default function SavedResponsesPanel({
  caseId,
  refreshNonce,
}: SavedResponsesPanelProps) {
  const { addToast } = useToastStore();
  const { user } = useAuthStore();
  const [items, setItems] = useState<SavedChatResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listSavedChatResponses(caseId);
      setItems(rows);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response
          ?.data?.detail || 'Failed to load saved responses';
      addToast(detail, 'error');
    } finally {
      setLoading(false);
    }
  }, [caseId, addToast]);

  useEffect(() => {
    load();
  }, [load, refreshNonce]);

  // An admin (operator/compliance) can delete any saved response; a regular
  // user can delete only their own (the backend enforces this; we mirror the
  // visibility so the button is not shown for forbidden deletes).
  function canDelete(s: SavedChatResponse): boolean {
    if (!user) return false;
    if (user.role === 'operator' || user.role === 'compliance') return true;
    return !!s.created_by && s.created_by === user.id;
  }

  async function handleDelete(id: string) {
    if (deletingId) return;
    if (!window.confirm('Delete this saved response? This cannot be undone.')) {
      return;
    }
    setDeletingId(id);
    try {
      await deleteSavedChatResponse(id);
      setItems((prev) => prev.filter((s) => s.id !== id));
      if (expanded === id) setExpanded(null);
      addToast('Saved response deleted', 'success');
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response
          ?.data?.detail || 'Failed to delete saved response';
      addToast(detail, 'error');
    } finally {
      setDeletingId(null);
    }
  }

  function handleExport(s: SavedChatResponse) {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts =
      `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
      `${pad(now.getHours())}${pad(now.getMinutes())}`;
    const blob = new Blob([s.content + '\n'], {
      type: 'text/markdown;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saved_${s.case_id}_${ts}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center text-slate-400">
        <Bookmark className="h-8 w-8 mb-2" />
        <p className="text-sm">
          No saved responses yet. Use the Save button on a chat response to
          keep it with this case.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((s) => {
        const isOpen = expanded === s.id;
        return (
          <div
            key={s.id}
            className="border border-slate-200 rounded-lg bg-white overflow-hidden"
          >
            <div className="flex items-start justify-between gap-3 px-4 py-3">
              <button
                onClick={() => setExpanded(isOpen ? null : s.id)}
                className="flex-1 text-left"
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[11px] text-slate-400">
                    {s.created_at
                      ? new Date(s.created_at).toLocaleString()
                      : ''}
                  </span>
                  {s.model_used && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                      {s.model_used}
                    </span>
                  )}
                  {s.path_used && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                      {s.path_used}
                    </span>
                  )}
                </div>
                {isOpen ? (
                  <div className="prose-sm max-w-none text-slate-800">
                    <MarkdownRenderer content={s.content} />
                  </div>
                ) : (
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans">
                    {preview(s.content)}
                  </pre>
                )}
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => handleExport(s)}
                  title="Download as .md"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
                {canDelete(s) && (
                  <button
                    onClick={() => handleDelete(s.id)}
                    disabled={deletingId === s.id}
                    title="Delete saved response"
                    className="inline-flex items-center justify-center h-7 w-7 rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {deletingId === s.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}